"use client";

import { useUserPreferencesStore } from "@/stores/user-preferences";
import { logger } from "@/utils/logger";
import { getUserPreferences } from "@/utils/user-preferences";
import { useEffect, useRef } from "react";

/**
 * Hook to sync selectedGroupId between IndexedDB and Zustand store
 *
 * This hook:
 * 1. Loads selectedGroupId from IndexedDB on mount (source of truth)
 * 2. Syncs it to the Zustand store for fast in-app access
 * 3. Listens for changes from other tabs via BroadcastChannel
 * 4. Updates the store when changes are detected
 *
 * Usage: Call once in your root layout or auth provider
 */
export function useSelectedGroupSync() {
  const setSelectedGroup = useUserPreferencesStore(state => state.setSelectedGroup);
  const channelRef = useRef<BroadcastChannel | null>(null);
  useEffect(() => {
    // 1. Load initial state from IndexedDB
    const loadFromIndexedDB = async () => {
      try {
        const preferences = await getUserPreferences();
        if (preferences.selectedGroupId || preferences.relationship) {
          setSelectedGroup({
            selectedGroupId: preferences.selectedGroupId,
            relationship: preferences.relationship
          });
        }
      } catch (error) {
        logger.error("Failed to load selectedGroupId from IndexedDB:", error);
      }
    };
    loadFromIndexedDB();

    // 2. Set up cross-tab synchronization using BroadcastChannel
    if (typeof window !== "undefined" && "BroadcastChannel" in window) {
      const channel = new BroadcastChannel("selectedGroup_sync");
      channelRef.current = channel;

      // Listen for messages from other tabs
      channel.onmessage = (event: MessageEvent) => {
        const data = event.data as unknown;
        if (!data || typeof data !== "object") return;
        const {
          type,
          payload
        } = data as {
          type?: string;
          payload?: {
            selectedGroupId?: string;
            relationship?: "buyer" | "seller";
          };
        };
        if (type !== "selectedGroup_updated" || !payload) return;
        setSelectedGroup({
          selectedGroupId: payload.selectedGroupId,
          relationship: payload.relationship
        });
      };
    }

    // 3. Cleanup
    return () => {
      if (channelRef.current) {
        channelRef.current.close();
        channelRef.current = null;
      }
    };
  }, [setSelectedGroup]);
}

/**
 * Helper function to broadcast selectedGroupId changes to other tabs
 *
 * Call this after updating IndexedDB to notify other tabs
 */
export function broadcastSelectedGroupChange(params: {
  selectedGroupId?: string;
  relationship?: "buyer" | "seller";
}) {
  if (typeof window !== "undefined" && "BroadcastChannel" in window) {
    try {
      const channel = new BroadcastChannel("selectedGroup_sync");
      channel.postMessage({
        type: "selectedGroup_updated",
        payload: params
      });
      // channel.close() is removed to prevent a race condition
    } catch (error) {
      logger.error("Failed to broadcast selectedGroup change:", error);
    }
  }
}